/* Slice VI-03 · Contractor-Reviewed Levi Estimate Suggestions Delta.
 *
 * Minimum functional review surface. Mounts inside the existing
 * estimate workflow. Never redesigns the estimate builder. Contractor
 * must explicitly commit selected accepted suggestions — nothing
 * enters the estimate on read, on prepare, or on per-item edit. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import {
  Sparkles, CheckCircle2, Pencil, XCircle, RotateCcw, Send, Loader2, Info,
} from "lucide-react";

const STATE_BADGE = {
  levi_preliminary:     "bg-amber-50 text-amber-900 border-amber-200",
  contractor_accepted:  "bg-emerald-50 text-emerald-800 border-emerald-200",
  contractor_edited:    "bg-emerald-50 text-emerald-800 border-emerald-200",
  contractor_rejected:  "bg-slate-100 text-slate-700 border-slate-200",
  committed_to_estimate:"bg-sky-50 text-sky-800 border-sky-200",
};

const fmt = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Number.isInteger(n) ? String(n) : (Math.round(n * 100) / 100).toString();
};

const CommitDialog = ({ open, items, targetName, onCancel, onConfirm, saving, t }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
         data-testid="vi03-commit-confirm">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-lg p-5">
        <div className="text-base font-medium mb-2">{t("vi03.commit.title")}</div>
        <div className="text-sm text-[var(--jc-text-soft)] mb-3">
          {t("vi03.commit.summary", { count: items.length, estimate: targetName || "" })}
        </div>
        <div className="rounded border bg-slate-50 p-3 text-sm mb-3 max-h-60 overflow-y-auto"
             data-testid="vi03-commit-summary-list">
          {items.map((it, i) => (
            <div key={it.id} className="flex justify-between py-1 border-b last:border-0"
                 data-testid={`vi03-commit-summary-item-${it.id}`}>
              <span className="truncate mr-2">{i + 1}. {it.__display_name}</span>
              <span className="tabular-nums text-[var(--jc-text-soft)]">
                {fmt(it.__q)} {it.__unit}
              </span>
            </div>
          ))}
        </div>
        <div className="text-xs text-[var(--jc-text-soft)] mb-4">
          · {t("vi03.commit.becomes_lines")}<br />
          · {t("vi03.commit.no_downstream")}
        </div>
        <div className="flex justify-end gap-2">
          <button className="px-3 py-1.5 rounded border text-sm"
                  onClick={onCancel} disabled={saving}
                  data-testid="vi03-commit-cancel">
            {t("vi03.actions.cancel")}
          </button>
          <button className="px-3 py-1.5 rounded text-white text-sm bg-[var(--jc-blue)] hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
                  onClick={onConfirm} disabled={saving}
                  data-testid="vi03-commit-confirm-btn">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {t("vi03.commit.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

const ItemCard = ({ item, t, onAccept, onEdit, onReject, onReset, busy }) => {
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState(item.contractor_edit?.quantity ?? item.levi_proposed?.quantity ?? "");
  const [u, setU] = useState(item.contractor_edit?.unit ?? item.levi_proposed?.unit ?? "");
  const [p, setP] = useState(item.contractor_edit?.unit_price ?? item.levi_proposed?.unit_price ?? "");
  const state = item.state || "levi_preliminary";
  const readOnly = state === "committed_to_estimate";
  const displayQ = item.contractor_edit?.quantity ?? item.levi_proposed?.quantity;
  const displayU = item.contractor_edit?.unit ?? item.levi_proposed?.unit;
  const displayName = item.contractor_edit?.name ?? item.levi_proposed?.name ?? "";

  const submitEdit = () => {
    const payload = {};
    const qn = Number(q); if (Number.isFinite(qn) && qn >= 0) payload.quantity = qn;
    if (u) payload.unit = u;
    const pn = Number(p); if (Number.isFinite(pn) && pn >= 0) payload.unit_price = pn;
    if (Object.keys(payload).length === 0) {
      toast.error(t("vi03.errors.no_editable_fields")); return;
    }
    onEdit(item, payload);
    setEditing(false);
  };

  return (
    <div className="border rounded-lg p-3 mb-2 bg-white"
         data-testid={`vi03-item-${item.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${STATE_BADGE[state] || ""}`}
                  data-testid={`vi03-state-${item.id}`}>
              {t(`vi03.state.${state}`)}
            </span>
            <span className="text-[11px] text-[var(--jc-text-soft)]"
                  data-testid={`vi03-source-wa-${item.id}`}>
              {t("vi03.item.from_work_area")}: {item.source_work_area_id}
            </span>
          </div>
          <div className="font-medium text-sm" data-testid={`vi03-item-name-${item.id}`}>
            {displayName}
          </div>
          <div className="text-sm text-[var(--jc-text-soft)]">
            {t("vi03.item.levi_proposed")}: <strong>{fmt(item.levi_proposed?.quantity)}</strong> {item.levi_proposed?.unit || ""}
            {item.contractor_edit && (
              <> · {t("vi03.item.your_edit")}: <strong data-testid={`vi03-edit-value-${item.id}`}>
                {fmt(displayQ)}</strong> {displayU}</>
            )}
          </div>
        </div>
      </div>

      {editing && !readOnly && (
        <div className="mt-2 p-2 rounded bg-slate-50 border" data-testid={`vi03-edit-form-${item.id}`}>
          <div className="flex flex-wrap gap-2 items-end text-xs">
            <label>{t("vi03.edit.quantity")}
              <input type="number" min="0" step="0.01" value={q}
                     onChange={(e) => setQ(e.target.value)}
                     className="block mt-0.5 px-2 py-1 border rounded w-24"
                     data-testid={`vi03-edit-quantity-${item.id}`} />
            </label>
            <label>{t("vi03.edit.unit")}
              <select value={u} onChange={(e) => setU(e.target.value)}
                      className="block mt-0.5 px-2 py-1 border rounded"
                      data-testid={`vi03-edit-unit-${item.id}`}>
                {["ft","in","sqft","linear_ft","each","cuft"].map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </select>
            </label>
            <label>{t("vi03.edit.unit_price")}
              <input type="number" min="0" step="0.01" value={p}
                     onChange={(e) => setP(e.target.value)}
                     className="block mt-0.5 px-2 py-1 border rounded w-24"
                     data-testid={`vi03-edit-price-${item.id}`} />
            </label>
          </div>
          <div className="mt-2 flex gap-2">
            <button className="px-3 py-1 rounded bg-[var(--jc-blue)] text-white text-sm"
                    onClick={submitEdit} disabled={busy}
                    data-testid={`vi03-edit-save-${item.id}`}>
              {t("vi03.edit.save")}
            </button>
            <button className="px-3 py-1 rounded border text-sm"
                    onClick={() => setEditing(false)} disabled={busy}
                    data-testid={`vi03-edit-cancel-${item.id}`}>
              {t("vi03.actions.cancel")}
            </button>
          </div>
        </div>
      )}

      {!readOnly && !editing && (
        <div className="mt-2 flex flex-wrap gap-2">
          {state !== "contractor_accepted" && state !== "contractor_edited" && (
            <button className="inline-flex items-center gap-1 px-3 py-1 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700"
                    onClick={() => onAccept(item)} disabled={busy}
                    data-testid={`vi03-accept-${item.id}`}>
              <CheckCircle2 size={12} />{t("vi03.actions.accept")}
            </button>
          )}
          <button className="inline-flex items-center gap-1 px-3 py-1 rounded border text-xs"
                  onClick={() => setEditing(true)} disabled={busy}
                  data-testid={`vi03-edit-btn-${item.id}`}>
            <Pencil size={12} />{t("vi03.actions.edit")}
          </button>
          {state !== "contractor_rejected" && (
            <button className="inline-flex items-center gap-1 px-3 py-1 rounded border text-xs text-red-700"
                    onClick={() => onReject(item)} disabled={busy}
                    data-testid={`vi03-reject-${item.id}`}>
              <XCircle size={12} />{t("vi03.actions.reject")}
            </button>
          )}
          {state !== "levi_preliminary" && (
            <button className="inline-flex items-center gap-1 px-3 py-1 rounded border text-xs"
                    onClick={() => onReset(item)} disabled={busy}
                    data-testid={`vi03-reset-${item.id}`}>
              <RotateCcw size={12} />{t("vi03.actions.reset")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const LeviEstimateSuggestionsSection = ({ projectId, estimateId, estimateName }) => {
  const { t } = useLang();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showCommit, setShowCommit] = useState(false);

  const prepare = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/estimate-suggestions/prepare`,
        { target_estimate_id: estimateId || null },
      );
      setDoc(data);
    } catch {
      toast.error(t("vi03.errors.prepare_failed"));
    } finally {
      setLoading(false);
    }
  }, [projectId, estimateId, t]);

  useEffect(() => { if (projectId) prepare(); }, [projectId, prepare]);

  const call = useCallback(async (item, action, body) => {
    if (busy || !doc) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/estimate-suggestions/${doc.id}/items/${item.id}/${action}`,
        body || {},
      );
      setDoc(data);
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      toast.error(t(`vi03.errors.${code || "generic"}`));
    } finally { setBusy(false); }
  }, [busy, doc, projectId, t]);

  const commit = useCallback(async () => {
    if (busy || !doc || !estimateId) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/estimate-suggestions/${doc.id}/commit`,
        { target_estimate_id: estimateId,
          request_key: `${doc.id}-${Date.now()}` },
      );
      setDoc(data);
      toast.success(t("vi03.toast.committed"));
      setShowCommit(false);
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      toast.error(t(`vi03.errors.${code || "commit_failed"}`));
    } finally { setBusy(false); }
  }, [busy, doc, projectId, estimateId, t]);

  const acceptable = useMemo(() => {
    if (!doc) return [];
    return (doc.items || []).filter(
      (it) => it.state === "contractor_accepted" || it.state === "contractor_edited",
    ).map((it) => ({
      ...it,
      __display_name: it.contractor_edit?.name ?? it.levi_proposed?.name,
      __q: it.contractor_edit?.quantity ?? it.levi_proposed?.quantity,
      __unit: it.contractor_edit?.unit ?? it.levi_proposed?.unit,
    }));
  }, [doc]);

  if (loading && !doc) {
    return <div className="text-xs text-[var(--jc-text-soft)] my-3" data-testid="vi03-loading">
      <Loader2 size={12} className="inline animate-spin mr-1" />{t("vi03.section.loading")}</div>;
  }
  if (!doc || (doc.items || []).length === 0) {
    return <div className="my-3 p-3 rounded border bg-slate-50 text-xs text-[var(--jc-text-soft)]"
                data-testid="vi03-empty">{t("vi03.section.empty")}</div>;
  }

  const committedInDoc = doc.status === "committed";

  return (
    <div className="my-4" data-testid="vi03-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[var(--jc-blue)]" />
          <div className="font-medium text-sm">{t("vi03.section.title")}</div>
          <span className="text-xs text-[var(--jc-text-soft)]" data-testid="vi03-item-count">
            {t("vi03.section.item_count", { count: (doc.items || []).length })}
          </span>
        </div>
        <button className="text-xs text-[var(--jc-blue)] underline"
                onClick={prepare} disabled={busy}
                data-testid="vi03-refresh">
          {t("vi03.section.refresh")}
        </button>
      </div>
      <div className="text-[11px] text-[var(--jc-text-soft)] mb-2 inline-flex items-center gap-1"
           data-testid="vi03-invariant-note">
        <Info size={11} />
        {t("vi03.section.invariant_note")}
      </div>
      {(doc.items || []).map((it) => (
        <ItemCard key={it.id} item={it} t={t} busy={busy}
                   onAccept={(x) => call(x, "accept", {})}
                   onEdit={(x, body) => call(x, "edit", body)}
                   onReject={(x) => call(x, "reject", {})}
                   onReset={(x) => call(x, "reset", {})} />
      ))}
      {!committedInDoc && (
        <div className="mt-2 flex items-center justify-end gap-2">
          <span className="text-xs text-[var(--jc-text-soft)]" data-testid="vi03-accepted-count">
            {t("vi03.section.accepted_count", { count: acceptable.length })}
          </span>
          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-sm bg-[var(--jc-blue)] hover:opacity-90 disabled:opacity-40"
                  onClick={() => setShowCommit(true)}
                  disabled={busy || acceptable.length === 0 || !estimateId}
                  data-testid="vi03-open-commit">
            <Send size={13} />{t("vi03.actions.commit")}
          </button>
        </div>
      )}
      {committedInDoc && (
        <div className="mt-2 text-xs text-emerald-800" data-testid="vi03-committed-badge">
          {t("vi03.section.committed_note")}
        </div>
      )}
      <CommitDialog
        open={showCommit}
        items={acceptable}
        targetName={estimateName}
        onCancel={() => setShowCommit(false)}
        onConfirm={commit}
        saving={busy}
        t={t} />
    </div>
  );
};

export default LeviEstimateSuggestionsSection;
